import { useState, useCallback, useRef, lazy, Suspense } from "react";
import { Compass, Map, ChevronLeft, MessageCircle, Sparkles, Globe, LogOut, Save, HelpCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLang } from "@/hooks/use-lang";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { usePremium } from "@/hooks/usePremium";
import ChatPanel, { type Msg } from "@/components/ChatPanel";
import ResultsPanel from "@/components/ResultsPanel";
import AuthModal from "@/components/AuthModal";
import InteractiveMap, { type InteractiveMapHandle } from "@/components/InteractiveMap";
import MapZoomOverlay from "@/components/MapZoomOverlay";
import { COUNTRY_COORDS } from "@/data/countryCoordinates";
import BottomNav, { type TabId } from "@/components/BottomNav";

const MobileChatSheet = lazy(() => import("@/components/MobileChatSheet"));
const MobileResultsView = lazy(() => import("@/components/MobileResultsView"));
const CountrySelector = lazy(() => import("@/components/CountrySelector"));
const LanguageSwitcher = lazy(() => import("@/components/LanguageSwitcher"));
const Feed = lazy(() => import("@/pages/Feed"));
const BudgetTravel = lazy(() => import("@/pages/BudgetTravel"));
const Favorites = lazy(() => import("@/pages/Favorites"));
import TravelTypePicker from "@/components/TravelTypePicker";

interface TripResults {
  city: string;
  dates: string;
  style: string;
  activities: { name: string; description: string; icon: string }[];
  hotels: { name: string; description: string; price: string }[];
  restaurants: { name: string; description: string; type: string }[];
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vibetrip-chat`;

type FlowMode = "country" | "guided" | null;

type SessionState = {
  flow_mode: FlowMode;
  travel_type: string | null;
};

const normalizeTravelType = (value: string): string | null => {
  const normalized = value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const aliases: Record<string, string[]> = {
    relax: ["relax", "relaxation", "detente", "chill"],
    adventure: ["adventure", "aventure"],
    culture: ["culture", "cultural", "culturel", "culturelle"],
    mixed: ["mixed", "mixte", "mix"],
  };

  for (const [key, words] of Object.entries(aliases)) {
    if (words.some((word) => normalized.includes(word))) {
      return key;
    }
  }

  return null;
};

const Index = () => {
  const { t, lang, toggleLang } = useLang();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user, session, signOut } = useAuth();
  const mapRef = useRef<InteractiveMapHandle>(null);
  const { isPremium, canGeneratePlan, incrementPlanCount, remainingPlans } = usePremium();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("home");

  const requireAuth = useCallback((action: () => void) => {
    if (user) { action(); return; }
    setAuthModalOpen(true);
  }, [user]);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<TripResults | null>(null);
  const [started, setStarted] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [flowMode, setFlowMode] = useState<FlowMode>(null);
  const [travelType, setTravelType] = useState<string | null>(null);
  // Zoom state
  const [zoomingCountry, setZoomingCountry] = useState<{ name: string; code: string } | null>(null);
  const zoomAbortRef = useRef(false);

  // Mobile-specific states
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileResultsOpen, setMobileResultsOpen] = useState(false);

  const handleReset = useCallback(() => {
    setIsResetting(true);
    setTimeout(() => {
      setMessages([]);
      setResults(null);
      setStarted(false);
      setMobileSheetOpen(false);
      setMobileResultsOpen(false);
      setShowTypePicker(false);
      setFlowMode(null);
      setTravelType(null);
      setIsResetting(false);
      setIsGeneratingTrip(false);
      mapRef.current?.reset();
    }, 400);
  }, []);

  const extractResults = (text: string): { cleanText: string; data: TripResults | null } => {
    const match = text.match(/===RESULTS_START===([\s\S]*?)===RESULTS_END===/);
    if (!match) return { cleanText: text, data: null };
    try {
      const jsonStr = match[1].trim();
      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
      const data = JSON.parse(jsonMatch ? jsonMatch[0] : jsonStr);
      const cleanText = text.replace(/===RESULTS_START===[\s\S]*?===RESULTS_END===/, "").trim();
      return { cleanText, data };
    } catch {
      return { cleanText: text, data: null };
    }
  };

  const [isGeneratingTrip, setIsGeneratingTrip] = useState(false);
  const isTravelTypeLocked = flowMode === "guided" && Boolean(travelType);

  const streamChat = useCallback(async (allMessages: Msg[], sessionStateOverride?: SessionState) => {
    const sessionState = sessionStateOverride ?? {
      flow_mode: flowMode,
      travel_type: travelType,
    };

    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ messages: allMessages, lang, session_state: sessionState }),
    });

    if (!resp.ok || !resp.body) {
      throw new Error(`Error ${resp.status}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullContent = "";
    let insideResults = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
        let line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (!line.startsWith("data: ")) continue;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") break;
        try {
          const parsed = JSON.parse(jsonStr);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            fullContent += delta;

            // Detect when results block begins — stop updating chat, show loader
            if (!insideResults && fullContent.includes("===RESULTS_START===")) {
              insideResults = true;
              setIsGeneratingTrip(true);
            }

            // Only update chat text BEFORE the results block
            if (!insideResults) {
              const displayText = fullContent.replace(/===RESULTS_START===[\s\S]*/g, "").trim();
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: displayText } : m);
                }
                return [...prev, { role: "assistant", content: displayText }];
              });
            }
          }
        } catch {
          buffer = line + "\n" + buffer;
          break;
        }
      }
    }

    // Final parse
    const { cleanText, data } = extractResults(fullContent);
    if (data) {
      setResults(data);
      incrementPlanCount();
      if (isMobile) setMobileResultsOpen(true);
    }
    setIsGeneratingTrip(false);

    // Set final clean message (without JSON)
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant") {
        return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: cleanText } : m);
      }
      return [...prev, { role: "assistant", content: cleanText }];
    });
  }, [lang, session?.access_token, isMobile, flowMode, travelType]);

  const handleSend = async (input: string) => {
    if (!started) setStarted(true);

    const detectedTravelType = normalizeTravelType(input);
    let nextFlowMode = flowMode;
    let nextTravelType = travelType;

    if (detectedTravelType) {
      nextTravelType = detectedTravelType;
      setTravelType(detectedTravelType);

      if (nextFlowMode !== "country") {
        nextFlowMode = "guided";
        setFlowMode("guided");
      }
    }

    const sessionState: SessionState = {
      flow_mode: nextFlowMode,
      travel_type: nextTravelType,
    };

    const userMsg: Msg = { role: "user", content: input };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      await streamChat(updatedMessages, sessionState);
    } catch (e) {
      console.error(e);
      toast({ title: t.errorTitle, description: t.errorMsg, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const startChatForCountry = useCallback((countryName: string) => {
    setZoomingCountry(null);
    setFlowMode("country");
    setTravelType(null);
    setShowTypePicker(false);
    setStarted(true);
    if (isMobile) setMobileSheetOpen(true);
    const prompt = lang === "fr"
      ? `Je veux voyager en ${countryName}`
      : `I want to travel to ${countryName}`;
    handleSend(prompt);
  }, [lang, isMobile, handleSend]);

  const handleCountrySelect = useCallback((countryName: string, countryCode: string) => {
    const coords = COUNTRY_COORDS[countryCode];
    if (!coords || !mapRef.current) {
      // Fallback: skip zoom, start chat directly
      startChatForCountry(countryName);
      return;
    }

    zoomAbortRef.current = false;
    setZoomingCountry({ name: countryName, code: countryCode });

    mapRef.current.flyTo(coords, 1.5).then(() => {
      if (!zoomAbortRef.current) {
        startChatForCountry(countryName);
      }
    });
  }, [startChatForCountry]);

  const handleSkipZoom = useCallback(() => {
    if (!zoomingCountry) return;
    zoomAbortRef.current = true;
    startChatForCountry(zoomingCountry.name);
  }, [zoomingCountry, startChatForCountry]);

  const handleDiscoveryMode = useCallback(() => {
    setFlowMode("guided");
    setTravelType(null);
    setShowTypePicker(true);
  }, []);

  const handleTravelTypeSelect = useCallback((type: string) => {
    const normalizedType = normalizeTravelType(type) ?? "mixed";

    setFlowMode("guided");
    setTravelType(normalizedType);
    setShowTypePicker(false);
    setStarted(true);
    if (isMobile) setMobileSheetOpen(true);

    const prompt = lang === "fr"
      ? `Type de voyage sélectionné : ${normalizedType}. Suggère directement 3 pays adaptés.`
      : `Selected travel type: ${normalizedType}. Suggest 3 matching countries directly.`;

    handleSend(prompt);
  }, [lang, isMobile, handleSend]);

  // ─── Non-home tabs ───
  if (activeTab !== "home") {
    return (
      <>
        <Suspense fallback={null}>
          {activeTab === "feed" && <Feed lang={lang} />}
          {activeTab === "budget" && <BudgetTravel lang={lang} />}
          {activeTab === "favorites" && <Favorites lang={lang} />}
        </Suspense>
        <BottomNav active={activeTab} onTabChange={setActiveTab} lang={lang} />
      </>
    );
  }

  // ─── STATE 1: Landing (not started) ───
  if (!started) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pb-20 relative overflow-hidden">
        <InteractiveMap ref={mapRef} />

        {/* Zoom overlay */}
        {zoomingCountry && (
          <MapZoomOverlay
            countryName={zoomingCountry.name}
            onSkip={handleSkipZoom}
          />
        )}

        {/* Language switcher + auth – top right */}
        {!zoomingCountry && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
            {user ? (
              <button
                onClick={signOut}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-background/60 backdrop-blur-xl border border-border/30 shadow-md hover:bg-background/80 transition-all text-muted-foreground"
                title="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-primary/80 backdrop-blur-xl border border-border/30 shadow-md hover:bg-primary transition-all text-primary-foreground"
              >
                {lang === "fr" ? "Connexion" : "Sign in"}
              </button>
            )}
            <button
              onClick={() => setFaqOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl text-sm font-semibold bg-background/60 backdrop-blur-xl border border-border/30 shadow-md hover:bg-background/80 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-foreground"
              aria-label="FAQ"
            >
              FAQ
            </button>
            <Suspense fallback={null}>
              <LanguageSwitcher lang={lang} onToggle={toggleLang} />
            </Suspense>
          </div>
        )}

        {!zoomingCountry && (
          <div className="text-center max-w-md space-y-6 relative z-10">
            <div className="w-16 h-16 rounded-2xl bg-primary/90 backdrop-blur-sm flex items-center justify-center mx-auto shadow-lg">
              <Compass className="w-8 h-8 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-foreground tracking-tight">{t.appName}</h1>
              <p className="text-muted-foreground text-lg mt-2">{t.tagline}</p>
            </div>
            {showTypePicker ? (
              <TravelTypePicker onSelect={handleTravelTypeSelect} />
            ) : (
              <div className="flex flex-col items-center gap-3 w-full max-w-xs mx-auto md:max-w-xs max-[767px]:max-w-[88%]">
                <button
                  onClick={() => setSelectorOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground h-14 px-6 rounded-2xl font-semibold text-base hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  <Globe className="w-5 h-5 shrink-0" />
                  {lang === "fr" ? "Choisir un pays" : "Choose a country"}
                </button>
                <button
                  onClick={handleDiscoveryMode}
                  className="w-full inline-flex items-center justify-center gap-2 h-14 px-6 rounded-2xl text-sm font-medium bg-background/60 backdrop-blur-xl border border-border/30 shadow-md hover:bg-background/80 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-foreground leading-tight text-center"
                >
                  <Sparkles className="w-4 h-4 text-primary shrink-0" />
                  <span className="line-clamp-2">{lang === "fr" ? "Je me laisse guider par le type de voyage" : "Guide me by travel type"}</span>
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {lang === "fr"
                ? "Sélectionne ta destination ou laisse-toi guider."
                : "Pick your destination or let us guide you."}
            </p>
          </div>
        )}

        <Suspense fallback={null}>
          <CountrySelector
            open={selectorOpen}
            onClose={() => setSelectorOpen(false)}
            onSelect={handleCountrySelect}
          />
        </Suspense>

        <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} lang={lang} />
        {faqOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setFaqOpen(false)}>
            <div className="bg-background rounded-2xl shadow-2xl border border-border/20 max-w-md w-[90%] p-6 space-y-4" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-foreground">FAQ</h2>
              <div className="space-y-3 text-sm text-muted-foreground max-h-[60vh] overflow-y-auto pr-1">
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Comment ça marche ?" : "How does it work?"}</p>
                  <p>{lang === "fr" ? "Choisis un pays ou laisse-toi guider par type de voyage. Notre IA crée un itinéraire personnalisé pour toi." : "Pick a country or let us guide you by travel type. Our AI builds a personalized itinerary for you."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Est-ce gratuit ?" : "Is it free?"}</p>
                  <p>{lang === "fr" ? "Oui, vous pouvez générer jusqu'à 2 plans de voyage gratuitement avec les activités. Pour débloquer les hôtels, restaurants et des plans illimités, passez à Premium." : "Yes, you can generate up to 2 travel plans for free with activities. To unlock hotels, restaurants, and unlimited plans, upgrade to Premium."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Dois-je créer un compte ?" : "Do I need an account?"}</p>
                  <p>{lang === "fr" ? "Non, mais un compte te permet de sauvegarder tes voyages favoris." : "No, but an account lets you save your favorite trips."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Quelles destinations sont disponibles ?" : "Which destinations are available?"}</p>
                  <p>{lang === "fr" ? "TravelMate couvre des centaines de destinations à travers le monde : Europe, Asie, Amériques, Afrique et Océanie." : "TravelMate covers hundreds of destinations worldwide: Europe, Asia, Americas, Africa, and Oceania."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Politique de confidentialité" : "Privacy policy"}</p>
                  <p>{lang === "fr" ? "Vos données personnelles sont protégées et ne sont jamais partagées avec des tiers. Nous utilisons vos informations uniquement pour améliorer votre expérience de voyage." : "Your personal data is protected and never shared with third parties. We only use your information to improve your travel experience."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Puis-je modifier mon itinéraire ?" : "Can I modify my itinerary?"}</p>
                  <p>{lang === "fr" ? "Oui, vous pouvez relancer une conversation à tout moment pour ajuster vos préférences et obtenir de nouvelles recommandations." : "Yes, you can restart a conversation anytime to adjust your preferences and get new recommendations."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Comment fonctionne le budget voyage ?" : "How does the travel budget work?"}</p>
                  <p>{lang === "fr" ? "Notre outil Budget Voyage estime les coûts (vols, hôtels, repas) selon votre ville de départ, la durée et le style de voyage." : "Our Budget Travel tool estimates costs (flights, hotels, meals) based on your departure city, duration, and travel style."}</p>
                </div>
                <div>
                  <p className="font-semibold text-foreground">{lang === "fr" ? "Comment contacter le support ?" : "How to contact support?"}</p>
                  <p>{lang === "fr" ? "Vous pouvez nous contacter via l'adresse email support@travelmate.app pour toute question ou suggestion." : "You can reach us at support@travelmate.app for any questions or suggestions."}</p>
                </div>
              </div>
              <button onClick={() => setFaqOpen(false)} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors">
                {lang === "fr" ? "Fermer" : "Close"}
              </button>
            </div>
          </div>
        )}
        <BottomNav active={activeTab} onTabChange={setActiveTab} lang={lang} />
      </div>
    );
  }

  // ─── MOBILE: Layered interface ───
  if (isMobile) {
    return (
      <div className={`h-screen relative transition-opacity duration-400 ${isResetting ? "opacity-0" : "opacity-100"}`}>
        <InteractiveMap ref={mapRef} />

        {/* Floating reset button */}
        <button
          onClick={handleReset}
          className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-medium bg-background/60 backdrop-blur-xl border border-border/30 shadow-lg hover:bg-background/80 transition-all text-foreground"
        >
          <Map className="w-4 h-4 text-primary" />
        </button>

        {/* Change destination */}
        <button
          onClick={() => setSelectorOpen(true)}
          className="fixed top-4 left-4 z-30 inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-medium bg-background/60 backdrop-blur-xl border border-border/30 shadow-lg text-foreground"
        >
          <Globe className="w-4 h-4 text-primary" />
        </button>

        {/* Bottom CTA buttons */}
        <div className="fixed bottom-6 left-4 right-4 z-30 flex gap-3">
          <button
            onClick={() => setMobileSheetOpen(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-4 rounded-2xl font-semibold text-base shadow-lg active:scale-[0.98] transition-transform"
          >
            <MessageCircle className="w-5 h-5" />
            {lang === "fr" ? "Continuer le chat" : "Continue chat"}
          </button>

          {results && (
            <button
              onClick={() => setMobileResultsOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-5 py-4 rounded-2xl font-semibold text-base shadow-lg active:scale-[0.98] transition-transform"
            >
              <Sparkles className="w-5 h-5" />
              {t.viewResults}
            </button>
          )}
        </div>

        <Suspense fallback={null}>
          <MobileChatSheet
            open={mobileSheetOpen}
            onClose={() => setMobileSheetOpen(false)}
            onReset={handleReset}
            messages={messages}
            onSend={handleSend}
            isLoading={isLoading}
            isGeneratingTrip={isGeneratingTrip}
            hasTravelType={isTravelTypeLocked}
          />
        </Suspense>

        {mobileResultsOpen && results && (
          <Suspense fallback={null}>
            <MobileResultsView
              results={results}
              onBack={() => setMobileResultsOpen(false)}
            />
          </Suspense>
        )}

        <Suspense fallback={null}>
          <CountrySelector
            open={selectorOpen}
            onClose={() => setSelectorOpen(false)}
            onSelect={handleCountrySelect}
          />
        </Suspense>

        <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} lang={lang} />
      </div>
    );
  }

  // ─── DESKTOP: Split layout ───
  return (
    <div className={`h-screen flex flex-col relative transition-opacity duration-400 ${isResetting ? "opacity-0" : "opacity-100"}`}>
      <InteractiveMap ref={mapRef} />

      {/* Floating reset button */}
      <button
        onClick={handleReset}
        className="fixed top-4 right-4 z-50 inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-medium bg-background/60 backdrop-blur-xl border border-border/30 shadow-lg hover:bg-background/80 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 text-foreground"
      >
        <Map className="w-4 h-4 text-primary" />
        {t.exploreAnother}
      </button>

      {/* Header */}
      <header className="relative z-10 px-4 py-3 flex items-center gap-3 shrink-0 bg-background/40 backdrop-blur-xl border-b border-border/10 shadow-sm">
        <button
          onClick={handleReset}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/80 backdrop-blur-sm hover:bg-primary transition-colors"
          title={t.exploreAnother}
        >
          <ChevronLeft className="w-4 h-4 text-primary-foreground" />
          <span className="text-sm font-medium text-primary-foreground">
            {lang === "fr" ? "Revenir en arrière" : "Go back"}
          </span>
        </button>
        <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center">
          <Compass className="w-4 h-4 text-primary-foreground" />
        </div>
        <h1 className="text-lg font-bold text-foreground">{t.appName}</h1>
        <div className="ml-auto mr-12 flex items-center gap-2">
          {results && (
            <button
              onClick={() => requireAuth(() => { /* save trip logic */ })}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/80 hover:bg-primary transition-colors text-sm font-medium text-primary-foreground"
            >
              <Save className="w-4 h-4" />
              {lang === "fr" ? "Sauvegarder" : "Save trip"}
            </button>
          )}
          {user ? (
            <button
              onClick={signOut}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/60 hover:bg-muted transition-colors text-sm font-medium text-foreground"
            >
              <LogOut className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setAuthModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              {lang === "fr" ? "Connexion" : "Sign in"}
            </button>
          )}
          <button
            onClick={() => setSelectorOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/60 hover:bg-muted transition-colors text-sm font-medium text-foreground"
          >
            <Globe className="w-4 h-4 text-primary" />
            {lang === "fr" ? "Changer de pays" : "Change country"}
          </button>
        </div>
      </header>

      {/* Desktop split content */}
      <div className="flex-1 flex flex-row min-h-0 relative z-10 p-3 gap-3">
        <div className="flex-1 max-w-[55%] flex flex-col min-h-0 bg-background/50 backdrop-blur-xl rounded-2xl border border-border/10 shadow-xl">
          <ChatPanel messages={messages} onSend={handleSend} isLoading={isLoading} isGeneratingTrip={isGeneratingTrip} hasTravelType={isTravelTypeLocked} />
        </div>
        <div className="flex-1 flex flex-col min-h-0 bg-background/50 backdrop-blur-xl rounded-2xl border border-border/10 shadow-xl">
          <ResultsPanel results={results} />
        </div>
      </div>

      <Suspense fallback={null}>
        <CountrySelector
          open={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          onSelect={handleCountrySelect}
        />
      </Suspense>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} lang={lang} />
    </div>
  );
};

export default Index;
